<template>
  <div class="wizard">
    <!-- 顶部信息栏 -->
    <header class="wz-top">
      <button type="button" class="wz-back" @click="$emit('back')">
        <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
          <path d="M19 12H6M12 6l-6 6 6 6" fill="none" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        重新选择
      </button>

      <div class="wz-meta">
        <strong>{{ subjectName || '未命名' }}</strong>
        <span>{{ scopeName }} · {{ roleName }} · {{ editionName }}</span>
      </div>

      <div class="wz-progress">
        <div class="wz-bar">
          <i :style="{ width: percent + '%' }"></i>
        </div>
        <span>{{ rated }} / {{ total }} 项</span>
      </div>
    </header>

    <!-- 权重面板：项目级可调，企业级固定 -->
    <el-collapse class="wz-weight" v-model="weightOpen">
      <el-collapse-item name="w" title="能力域权重设置">
        <WeightPanel
          :domains="domains"
          :weights="weights"
          :editable="scope === 'project'"
          @update:weights="(v) => $emit('update:weights', v)"
        />
      </el-collapse-item>
    </el-collapse>

    <div class="wz-body">
      <!-- 能力域步骤轨 -->
      <nav class="wz-rail" aria-label="能力域">
        <button
          v-for="(g, i) in groups"
          :key="g.id"
          type="button"
          class="rail-item"
          :class="{
            'is-active': i === step,
            'is-done': g.rated === g.indicators.length,
            'is-critical': g.critical
          }"
          @click="$emit('update:step', i)"
        >
          <span class="rail-idx">{{ i + 1 }}</span>
          <span class="rail-body">
            <span class="rail-name">
              {{ g.shortName }}
              <em v-if="g.critical">关键</em>
            </span>
            <span class="rail-prog">{{ g.rated }} / {{ g.indicators.length }}</span>
          </span>
        </button>
      </nav>

      <!-- 当前能力域的指标卡片 -->
      <section class="wz-main">
        <div class="wz-domain">
          <h2>{{ current.shortName }}</h2>
          <p>
            共 {{ current.indicators.length }} 项指标，权重 {{ pct(current.weight) }}。
            档位为递进关系：高分档已涵盖并满足低分档的要求。
          </p>
        </div>

        <IndicatorCard
          v-for="(ind, i) in current.indicators"
          :key="ind.id"
          :indicator="ind"
          :domain-name="current.shortName"
          :index="i"
          :value="valueOf(ind.id)"
          :evidence="evidenceOf(ind.id)"
          @update="(patch) => $emit('update-score', { indicatorId: ind.id, patch })"
        />

        <footer class="wz-foot">
          <el-button :disabled="step === 0" round @click="$emit('update:step', step - 1)">
            上一步
          </el-button>
          <el-button
            v-if="step < groups.length - 1"
            type="primary"
            round
            @click="$emit('update:step', step + 1)"
          >
            下一步：{{ groups[step + 1].shortName }}
          </el-button>
          <el-button v-else type="primary" round @click="onGenerate">
            生成评价报告
          </el-button>
        </footer>

        <!-- 漏评提示：点击生成报告但仍有未评项时，精确列出缺哪些（调研类网页通用逻辑） -->
        <div v-if="showMissing" class="wz-missing">
          <div class="wm-head">
            <span class="wm-title">还有 {{ missingCount }} 项未评分</span>
            <span class="wm-sub">未评分项不计入得分，建议补全后再生成报告</span>
          </div>
          <ul class="wm-list">
            <li v-for="g in missingByDomain" :key="g.step" class="wm-group">
              <button type="button" class="wm-domain" @click="jumpTo(g.step)">
                {{ g.domain }}
                <em>（{{ g.items.length }} 项）→</em>
              </button>
              <span class="wm-items">
                <button
                  v-for="it in g.items"
                  :key="it.id"
                  type="button"
                  class="wm-item"
                  @click="jumpTo(g.step)"
                >
                  {{ it.name }}
                </button>
              </span>
            </li>
          </ul>
          <div class="wm-actions">
            <el-button round @click="showMissing = false">返回补全</el-button>
            <el-button round type="primary" plain @click="forceFinish">
              仍要生成报告（未评分项不计入）
            </el-button>
          </div>
        </div>

        <p v-else-if="step === groups.length - 1 && !allRated" class="wz-tip">
          仍有 {{ missingCount }} 项未评分，未评分项不计入得分，建议完成后再生成报告。
        </p>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, nextTick } from 'vue'
import IndicatorCard from './IndicatorCard.vue'
import WeightPanel from './WeightPanel.vue'
import { DOMAINS, SCOPES, EDITIONS, ROLE_MAP, getEvidence } from '../data/index.js'

const props = defineProps({
  indicators: { type: Array, required: true },
  scores: { type: Object, default: () => ({}) },
  weights: { type: Object, required: true },
  step: { type: Number, default: 0 },
  scope: { type: String, default: 'project' },
  edition: { type: String, default: 'online' },
  roleId: { type: String, default: '' },
  subjectName: { type: String, default: '' }
})

const emit = defineEmits(['back', 'finish', 'update:step', 'update-score', 'update:weights'])

const weightOpen = ref([])
const showMissing = ref(false)

/** 未评分项：按能力域分组，便于在漏评提示中点名并一键跳转 */
const missingByDomain = computed(() => {
  const out = []
  groups.value.forEach((g, step) => {
    const items = g.indicators
      .filter((ind) => {
        const v = props.scores[ind.id]
        return !(v && typeof v.level === 'number')
      })
      .map((ind) => ({ id: ind.id, name: ind.name }))
    if (items.length) out.push({ domain: g.shortName, step, items })
  })
  return out
})

const missingCount = computed(() => missingByDomain.value.reduce((s, g) => s + g.items.length, 0))

/** 生成报告：全部评完直接出报告；有漏评则列出具体缺项，不前进 */
function onGenerate () {
  if (allRated.value) {
    showMissing.value = false
    emit('finish')
  } else {
    showMissing.value = true
  }
}

/** 仍要生成报告：忽略漏评，直接出报告（未评分项不计入得分） */
function forceFinish () {
  showMissing.value = false
  emit('finish')
}

function jumpTo (step) {
  showMissing.value = false
  emit('update:step', step)
}

// 切换能力域（下一步 / 上一步 / 点击步骤轨 / 漏评跳转）后回到页面顶部，
// 避免停留在上一页尾部，造成「进了下一页却还在页尾」的困惑
watch(
  () => props.step,
  () => {
    nextTick(() => window.scrollTo({ top: 0, behavior: 'auto' }))
  }
)

const domains = DOMAINS

const groups = computed(() =>
  DOMAINS.map((d) => {
    const list = props.indicators.filter((i) => i.domain === d.id)
    const rated = list.filter((i) => {
      const v = props.scores[i.id]
      return v && typeof v.level === 'number'
    }).length
    return { ...d, indicators: list, rated }
  })
)

const current = computed(() => groups.value[props.step] || groups.value[0])
const total = computed(() => props.indicators.length)
const rated = computed(() => groups.value.reduce((s, g) => s + g.rated, 0))
const percent = computed(() => (total.value ? Math.round((rated.value / total.value) * 100) : 0))
const allRated = computed(() => rated.value === total.value)

const scopeName = computed(() => (SCOPES[props.scope] || {}).name || '')
const editionName = computed(() => (EDITIONS[props.edition] || {}).name || '')
const roleName = computed(() => (ROLE_MAP[props.roleId] || {}).name || '')

function valueOf (id) {
  return props.scores[id] || { level: null, answers: {}, note: '', files: [] }
}

function evidenceOf (id) {
  return getEvidence(props.scope, props.edition, props.roleId, id)
}

function pct (v) {
  return `${((v || 0) * 100).toFixed(1)}%`
}
</script>

<style lang="scss" scoped>
.wizard {
  max-width: 1320px;
  margin: 0 auto;
  padding: 20px 24px 72px;
}

.wz-top {
  display: flex;
  align-items: center;
  gap: 20px;
  padding-bottom: 18px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.wz-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px 7px 12px;
  font-size: 13px;
  color: #06c;
  background: rgba(0, 113, 227, 0.08);
  border: 0;
  border-radius: 999px;
  cursor: pointer;

  &:hover {
    background: rgba(0, 113, 227, 0.16);
  }
}

.wz-meta {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;

  strong {
    font-size: 16px;
    font-weight: 600;
    color: #1d1d1f;
  }

  span {
    font-size: 12.5px;
    color: #86868b;
  }
}

.wz-progress {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 0 0 auto;

  span {
    font-size: 12.5px;
    color: #6e6e73;
    white-space: nowrap;
  }
}

.wz-bar {
  width: 160px;
  height: 5px;
  border-radius: 3px;
  background: rgba(120, 120, 128, 0.16);
  overflow: hidden;

  i {
    display: block;
    height: 100%;
    border-radius: 3px;
    background: linear-gradient(90deg, #0071e3, #5ac8fa);
    transition: width 0.4s cubic-bezier(0.22, 1, 0.36, 1);
  }
}

.wz-weight {
  margin-top: 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  overflow: hidden;
  background: #ffffff;
}

.wz-body {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 28px;
  margin-top: 24px;
}

.wz-rail {
  position: sticky;
  top: 20px;
  align-self: start;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.rail-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  text-align: left;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.22s ease;

  &:hover {
    background: rgba(120, 120, 128, 0.07);
  }

  &.is-active {
    background: #ffffff;
    border-color: rgba(0, 113, 227, 0.4);
    box-shadow: 0 6px 18px rgba(0, 113, 227, 0.10);
  }
}

.rail-idx {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  font-size: 12.5px;
  font-weight: 600;
  color: #86868b;
  background: rgba(120, 120, 128, 0.12);
  border-radius: 50%;
  transition: all 0.22s ease;

  .rail-item.is-active & {
    color: #ffffff;
    background: #0071e3;
  }

  .rail-item.is-done & {
    color: #ffffff;
    background: #34c759;
  }
}

.rail-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.rail-name {
  font-size: 13.5px;
  font-weight: 500;
  color: #1d1d1f;

  em {
    margin-left: 6px;
    padding: 1px 5px;
    font-style: normal;
    font-size: 10.5px;
    color: #d1681f;
    background: rgba(209, 104, 31, 0.10);
    border-radius: 3px;
  }
}

.rail-prog {
  font-size: 11.5px;
  color: #86868b;
}

.wz-main {
  min-width: 0;
}

.wz-domain {
  margin-bottom: 16px;

  h2 {
    margin: 0;
    font-size: clamp(20px, 2.6vw, 27px);
    font-weight: 600;
    color: #1d1d1f;
  }

  p {
    margin: 8px 0 0;
    font-size: 13px;
    line-height: 1.8;
    color: #6e6e73;
  }
}

.wz-main > :deep(.indicator) + :deep(.indicator) {
  margin-top: 14px;
}

.wz-foot {
  display: flex;
  justify-content: center;
  gap: 14px;
  margin-top: 28px;
}

.wz-tip {
  margin: 14px 0 0;
  text-align: center;
  font-size: 12.5px;
  color: #d1681f;
}

.wz-missing {
  margin: 18px 0 0;
  padding: 18px 20px;
  background: #fff8f1;
  border: 1px solid rgba(209, 104, 31, 0.32);
  border-radius: 14px;
}

.wm-head {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}

.wm-title {
  font-size: 15px;
  font-weight: 600;
  color: #d1681f;
}

.wm-sub {
  font-size: 12.5px;
  color: #a16207;
}

.wm-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.wm-group {
  & + & {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px dashed rgba(209, 104, 31, 0.24);
  }
}

.wm-domain {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 0;
  font-size: 13px;
  font-weight: 600;
  color: #1d1d1f;
  background: transparent;
  border: 0;
  cursor: pointer;

  em {
    font-style: normal;
    font-size: 11.5px;
    font-weight: 500;
    color: #0071e3;
  }

  &:hover {
    text-decoration: underline;
  }
}

.wm-items {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.wm-item {
  padding: 4px 11px;
  font-size: 12px;
  color: #6e6e73;
  background: rgba(120, 120, 128, 0.10);
  border: 0;
  border-radius: 999px;
  cursor: pointer;

  &:hover {
    color: #0071e3;
    background: rgba(0, 113, 227, 0.10);
  }
}

.wm-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
}

@media (max-width: 980px) {
  .wz-body {
    grid-template-columns: minmax(0, 1fr);
  }

  .wz-rail {
    position: static;
    flex-direction: row;
    overflow-x: auto;
    padding-bottom: 6px;
  }

  .rail-item {
    flex: 0 0 auto;
  }

  .wz-top {
    flex-wrap: wrap;
    gap: 12px;
  }
}
</style>
